import type { ConsentChoice } from './consent';
import type { RuntimeConfig } from './runtime-config';

type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & { dataLayer?: unknown[]; gtag?: Gtag; [key: `ga-disable-${string}`]: boolean | undefined };

const advertisingDenied = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };

// A new route must be deliberately added here before it can use Analytics.
export function isAnalyticsPage(pathname: string): boolean {
  return pathname === '/' || pathname === '/index.html';
}

function analyticsCookieNames(): string[] {
  return document.cookie.split(';').map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => /^_ga(?:_|$)|^_gid$|^_gat(?:_|$)/.test(name));
}

export function removeAnalyticsCookies(): void {
  if (typeof document === 'undefined' || typeof location === 'undefined') return;
  const domains = new Set<string | null>([null]);
  const host = location.hostname.toLowerCase();
  if (host === 'burnout.com.br' || host.endsWith('.burnout.com.br')) {
    domains.add('burnout.com.br');
    domains.add('.burnout.com.br');
    if (host !== 'burnout.com.br') domains.add(host);
  }
  // GA uses path=/ here. Include ancestor paths in case an older tag used one.
  const paths = new Set(['/']);
  const parts = location.pathname.split('/').filter(Boolean);
  for (let i = 1; i <= parts.length; i += 1) paths.add(`/${parts.slice(0, i).join('/')}`);
  for (const name of analyticsCookieNames()) {
    for (const path of paths) {
      for (const domain of domains) {
        document.cookie = `${name}=; Max-Age=0; Path=${path}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
      }
    }
  }
}

export function startAnalytics(runtime: RuntimeConfig, initialChoice: ConsentChoice | null) {
  const allowed = runtime.mode === 'prod' && Boolean(runtime.gaMeasurementId)
    && typeof window !== 'undefined' && isAnalyticsPage(window.location.pathname);
  let active = false;
  const target = allowed ? window as unknown as AnalyticsWindow : null;
  const disableKey = `ga-disable-${runtime.gaMeasurementId}` as const;

  function enable(): void {
    if (!target || active) return;
    active = true;
    target[disableKey] = false;
    target.dataLayer = target.dataLayer || [];
    target.gtag = target.gtag || function gtag() {
      // Google Tag requires the native Arguments object; a rest-parameter Array is ignored.
      // eslint-disable-next-line prefer-rest-params
      target.dataLayer?.push(arguments);
    };
    target.gtag('consent', 'default', { analytics_storage: 'granted', ...advertisingDenied });
    target.gtag('js', new Date());
    // Do not pass arbitrary query strings, fragments or referrer URLs to GA.
    target.gtag('config', runtime.gaMeasurementId, {
      page_location: `${location.origin}${location.pathname}`,
      page_path: location.pathname,
      page_referrer: '',
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    if (!document.querySelector('script[data-burnout-analytics]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(runtime.gaMeasurementId)}`;
      script.dataset.burnoutAnalytics = 'true';
      document.head.appendChild(script);
    }
  }

  if (initialChoice === 'accepted') enable();
  else removeAnalyticsCookies();

  return {
    // A previously loaded tag cannot be unloaded safely. The caller reloads after disabling it.
    update(choice: ConsentChoice): boolean {
      if (choice === 'accepted') { enable(); return false; }
      if (active && target) target[disableKey] = true;
      removeAnalyticsCookies();
      return active;
    },
    clear(): boolean {
      if (active && target) target[disableKey] = true;
      removeAnalyticsCookies();
      return active;
    },
  };
}
